import { useState } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"
import moment from "moment"
import ChevronDownIcon from "shared/components/icons/chevron_down_icon"
import ChevronUpIcon from "shared/components/icons/chevron_up_icon"

import styles from "./step_details.module.scss"
import DelayIcon from "shared/components/icons/delay_icon"
import { formatToTimestamp } from "../diy_composer/utils/format_time"

const RunDetails = ({ stepFields, message, messageTitle, isError }) => {
  // stepFields may be nested (ex. query results, which is an array of arrays) or flat (single array)
  const isNested = stepFields?.length > 0 && Array.isArray(stepFields[0])
  const groups = isNested ? stepFields : stepFields?.length ? [stepFields] : []
  const hasStepFields = groups.length > 0 && groups.some(group => group?.length > 0)
  const hasMessage = !!message

  if (!hasStepFields && !hasMessage) {
    return "No details available"
  }

  return (
    <>
      {hasMessage &&
        (isError ? (
          <div className={styles.message}>
            <div className={styles.title}>{messageTitle}</div>
            <div>{message}</div>
          </div>
        ) : (
          <FieldDetails label={messageTitle || "Details"} value={message} />
        ))}

      {hasStepFields && groups.map((group, groupIdx) => (
        <>
          {group.map(field => (
            <FieldDetails key={field.label} {...field} />
          ))}
          {groupIdx < groups.length - 1 && <hr />}
        </>
      ))}
    </>
  )
}

RunDetails.propTypes = {
  stepFields: PropTypes.array,
  message: PropTypes.string,
  messageTitle: PropTypes.string,
  isError: PropTypes.bool,
}

const FieldDetails = ({ label, value, unfilteredValue }) => (
  <div className={styles["detail-pair"]}>
    <dt className="body-caption">{label}</dt>
    <dd>
      <code>{value || "(none)"}</code>
      {unfilteredValue && (
        <div className={styles.unfiltered}>
          {`The original value "${unfilteredValue}" was updated by the filter.`}
        </div>
      )}
    </dd>
  </div>
)

FieldDetails.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  unfilteredValue: PropTypes.string,
}

const verbForOutcome = outcome => {
  switch (outcome) {
    case "success":
      return "ran"
    case "error":
      return "failed"
    case "abort":
      return "aborted"
    case "filter_skip":
      return "skipped"
  }
}

const StepDetails = ({
  brand_color,
  icon_url,
  firstStep,
  finalStep,
  id,
  name,
  service_name,
  detailsForStep,
}) => {
  const [showDetails, setShowDetails] = useState(false)

  let stepType
  if (id.match(/^\/actions\//)) {
    stepType = "Action"
  } else if (id.match(/^\/triggers\//)) {
    stepType = "Trigger"
  } else if (id.match(/^\/queries\//)) {
    stepType = "Query"
  } else if (id.match(/^filter$/)) {
    stepType = "Filter Code"
  } else if (id.match(/^smart_fill$/)) {
    stepType = "AI Action assist"
  } else {
    stepType = "Delayed"
  }
  const timeString = detailsForStep?.ts
  const timestamp = timeString && moment(timeString).format("h:mm A")

  const subheaderName = () => {
    if (service_name === "Delay") {
      let timestamp = formatToTimestamp(detailsForStep.actions_delay)
      return `${timestamp} ${name}`
    } else {
      return name
    }
  }

  const message = detailsForStep?.message
  const messageTitle = detailsForStep?.message_title

  const didNotRun = !["success", "error"].includes(detailsForStep?.outcome)

  let stepFields
  if (detailsForStep?.ingredients?.length > 0) {
    stepFields = detailsForStep.ingredients
  } else if (detailsForStep?.query_ingredients?.length > 0) {
    stepFields = detailsForStep.query_ingredients
  } else if (detailsForStep?.action_fields?.length > 0) {
    stepFields = detailsForStep.action_fields
  } else {
    stepFields = detailsForStep?.fields
  }
  const isGrouped = stepFields?.length > 0 && Array.isArray(stepFields[0])

  if (detailsForStep?.unfiltered_action_fields?.length > 0) {
    const withUnfilteredValue = field => {
      const unfilteredField = detailsForStep.unfiltered_action_fields.find(
        ({ label }) => label === field.label
      )
      const unfilteredValue =
        unfilteredField?.value !== field.value ? unfilteredField.value : null
      return { ...field, unfilteredValue }
    }

    stepFields = isGrouped
      ? stepFields?.map(group => group.map(withUnfilteredValue))
      : stepFields?.map(withUnfilteredValue)
  }

  const outcomeVerb = verbForOutcome(detailsForStep?.outcome)
  const errorOutcome = ["error", "abort"].includes(detailsForStep?.outcome)

  const hasDetails = !!message || stepFields?.length > 0
  const expandable = id === "smart_fill" ? hasDetails : service_name !== "Delay"

  const onClick = async () => {
    if (!expandable) return

    const toggledShow = !showDetails
    setShowDetails(toggledShow)
  }

  return (
    <div className={styles.step}>
      <div className={styles.rail}>
        {!firstStep && <div className={styles["top-line"]} />}
        <div className={styles.icon} style={{ backgroundColor: brand_color }}>
          {service_name === "Delay" ? (
            <DelayIcon title="delay icon" />
          ) : (
            <img src={icon_url} alt={`${service_name} icon`} />
          )}
        </div>
        {!finalStep && <div className={styles["bottom-line"]} />}
      </div>
      <div className={classnames(styles.content, { [styles.first]: firstStep })}>
        <div className={styles.overview} onClick={onClick}>
          <h4>{service_name}</h4>
          {expandable ? (
            <button
              type="button"
              className={styles["show-details"]}
              aria-expanded={showDetails}
              aria-label={`${showDetails ? "Hide" : "Show"} ${service_name} details`}
            >
              {showDetails ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </button>
          ) : null}
        </div>
        <div onClick={onClick} className={styles.subheader}>
          <p className="body-small">{subheaderName()}</p>
          <div className={classnames(styles.timestamp, { [styles.error]: errorOutcome })}>
            <div className={classnames(styles.dot, { [styles.open]: didNotRun })} />
            <span className="body-small">
              {outcomeVerb ? (
                <>
                  {service_name === "Delay" ? (
                    <>
                      {stepType}
                      {timestamp && ` until ${timestamp}`}
                    </>
                  ) : (
                    <>
                      {stepType} {outcomeVerb}
                      {timestamp && `, ${timestamp}`}
                    </>
                  )}
                </>
              ) : (
                <>Expand for details</>
              )}
            </span>
          </div>
        </div>
        {showDetails && detailsForStep && (
          <dl className={styles.details}>
            <RunDetails
              stepFields={stepFields}
              message={message}
              messageTitle={messageTitle}
              isError={errorOutcome}
            />
          </dl>
        )}
      </div>
    </div>
  )
}

StepDetails.propTypes = {
  brand_color: PropTypes.string.isRequired,
  icon_url: PropTypes.string.isRequired,
  firstStep: PropTypes.bool.isRequired,
  finalStep: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  service_name: PropTypes.string.isRequired,
  detailsForStep: PropTypes.shape({
    action_fields: PropTypes.array,
    fields: PropTypes.array,
    ingredients: PropTypes.array,
    query_ingredients: PropTypes.array,
    message: PropTypes.string,
    message_title: PropTypes.string,
    outcome: PropTypes.string,
    stepFields: PropTypes.array,
    ts: PropTypes.string,
    unfiltered_action_fields: PropTypes.array,
    actions_delay: PropTypes.number,
  }),
}

export default StepDetails
